import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AppBottomSheetSurface, AppButton, AppSection, AppText } from "../../ui-foundation";

const REASONS = [
  "It's spam or advertising",
  "It's offensive or harmful",
  "It's misinformation",
  "It targets a specific person",
  "It's not relevant to single parent families",
  "Other",
];

interface ReportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentType?: string;
  contentId?: string;
  reportedUserId?: string;
}

const ReportSheet = ({ open, onOpenChange, contentType, contentId, reportedUserId }: ReportSheetProps) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!selected || !user) return;
    setSubmitting(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await supabase.from("reports" as any).insert({
      reporter_id: user.id,
      reported_user_id: reportedUserId || null,
      content_type: contentType || null,
      content_id: contentId || null,
      reason: selected,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    setSubmitting(false);
    setSelected(null);
    onOpenChange(false);
    toast.success("Thanks — we've received your report and will review it.");
  };

  return (
    <AppBottomSheetSurface
      open={open}
      onOpenChange={onOpenChange}
      title="Why are you reporting this?"
      description="Choose the closest reason so the moderation team can review it quickly."
      footer={
        <AppButton
          onPress={() => void submit()}
          disabled={!selected || submitting}
          width="100%"
        >
          {submitting ? "Submitting..." : "Submit report"}
        </AppButton>
      }
    >
      <AppSection gap="$xs">
          {REASONS.map((r) => (
            <AppButton
              key={r}
              type="button"
              size="lg"
              tone={selected === r ? "primary" : "outline"}
              onPress={() => setSelected(r)}
              width="100%"
              justifyContent="flex-start"
            >
              <AppText color={selected === r ? "$primaryForeground" : "$color"} fontSize="$sm" lineHeight="$md">
                {r}
              </AppText>
            </AppButton>
          ))}
      </AppSection>
    </AppBottomSheetSurface>
  );
};

export default ReportSheet;
